import { registerPlugin, Capacitor } from '@capacitor/core'

/**
 * Мост к нативной кнопке Alfa Pay (AlfaPay SDK для iOS/Android) через Capacitor.
 *
 * Зачем нативно, а не в WebView: кнопка умеет диплинком уходить в приложение
 * Альфа-Банка, а если его нет — сама переключается на веб-версию или оплату по
 * уведомлению на телефон. В нашем WebView это не работает.
 *
 * ⚠️ Кнопка SDK **сама регистрирует заказ в шлюзе** по открытому токену мерчанта —
 * наш `register.do` в этом сценарии не вызывается. Поэтому:
 *
 * - `orderNumber` обязателен и должен быть ровно тем, что отдал бэкенд: по нему
 *   callback банка находит наш заказ (mdOrder нам заранее неизвестен);
 * - факт оплаты подтверждает НЕ результат этого вызова, а статус заказа с бэкенда —
 *   ровно как и в обычном сценарии (см. `usePaymentReconciliation`).
 *
 * Конфигурацию отдаёт `POST /payments/alfabank/alfapay-config`, руками её собирать нельзя:
 * открытый токен, домен шлюза и урлы возврата живут в настройках заведения.
 */
export interface AlfaPayNativeConfig {
  /** Открытый (клиентский) токен мерчанта. */
  token: string
  /** Сумма в копейках: 10,42 ₽ = 1042. */
  amount: number
  /** Номер заказа в нашей системе — связь callback'а с заказом. */
  orderNumber: string
  returnUrl: string
  failUrl: string
  /** Только `payment.alfabank.ru` либо `pay.alfabank.ru` — тестового домена у кнопки нет. */
  domain: string
  description?: string
  email?: string
  phone?: string
  /** Идентификатор клиента: нужен банку для работы со связками карт. */
  clientId?: string
}

/** Чем закончился нативный экран оплаты. */
export type AlfaPayNativeStatus =
  /** Клиент ушёл в приложение банка / на веб-форму. Оплату подтверждает бэкенд. */
  | 'started'
  /** Клиент закрыл экран, не начав оплату. */
  | 'cancelled'
  /** Кнопка или шлюз вернули ошибку. */
  | 'failed'

export interface AlfaPayNativeResult {
  status: AlfaPayNativeStatus
  /** Текст ошибки от SDK — показывать можно, но решение об оплате принимает бэкенд. */
  message?: string
}

export interface AlfaPayNativePlugin {
  /**
   * Открывает нативный экран оплаты с кнопкой Alfa Pay.
   * Промис резолвится, когда экран закрыт, — не когда прошла оплата.
   */
  present(config: AlfaPayNativeConfig): Promise<AlfaPayNativeResult>

  /**
   * Установлено ли приложение Альфа-Банка (проверка схем из `LSApplicationQueriesSchemes`
   * / `<queries>`). Нужно только для текста подсказки: без приложения кнопка всё равно
   * работает через веб-версию.
   */
  isBankAppInstalled(): Promise<{ installed: boolean }>
}

export const AlfaPayNative = registerPlugin<AlfaPayNativePlugin>('AlfaPayNative')

/** Доступна ли нативная кнопка Alfa Pay на текущей платформе. */
export const isAlfaPayNativeAvailable = (): boolean => {
  try {
    return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('AlfaPayNative')
  } catch {
    return false
  }
}
